import * as React from 'react';
import classNames from 'classnames';
import {
  BoundedContent,
  Box,
  StandardTile,
  StandardTileContents,
  StandardTileDescription,
  StandardTileImage,
  StandardTileName,
  StandardTileOverlay,
  Typography,
  Span,
} from '@vp/swan';
import { isEmptyNavItem } from '../isEmptyNavItem';
import { useNavContext } from '../NavContext';
import { useHoverIntent } from '../../../../hooks/useHoverIntent';
import { Image } from '../../../Image';
import { FlyoutWrapper } from './FlyoutWrapper';
import { useIsSsrContext } from '../../../../contexts/IsSsrContext';
import TrackableLink from '../../../TrackableLink';
import { BookendsBadge } from '../../../BookendsBadge';
import {
  LinkList,
  MenuTileProps,
  NavItem,
  NavItemWithChildrenType,
  SubCategoryEntryProps,
  TileMenuNavFlyout,
  ThirdLevelNavItemListProps,
} from '../../../../types';
import { SeeAllBar } from './SeeAllBar';

const hasRenderableChildren = (subCategory: NavItem) => {
  return (
    !isEmptyNavItem(subCategory) &&
    (subCategory as NavItemWithChildrenType).children?.filter(
      (subflyoutLink) =>
        subflyoutLink.url && subflyoutLink.image && subflyoutLink.image.url
    ).length > 0
  );
};

export const TileMenuNavigationFlyout = (
  props: TileMenuNavFlyout &
    LinkList<NavItem> &
    React.ComponentProps<'section'> & {
      parentNavigationDetailPath: string[];
    }
) => {
  const {
    focusedSubcategoryIdx,
    focusedThirdLevelIdx,
    focusAfterRender,
    isMxNav,
  } = useNavContext();
  const { markFlyoutHovered, isFlyoutHovered } = useIsSsrContext();
  const seeAllRef = React.useRef<HTMLAnchorElement>(null);
  const flyoutId = props.id || `tile-menu-flyout-${props.parentNavigationDetailPath.join('-')}`;

  // Treat the flyout as "hovered" if either:
  // 1. It has been explicitly hovered before, OR
  // 2. It is currently in the focused category (prevents FOUC on quick switches)
  const hasBeenHovered = isFlyoutHovered(flyoutId) || props.isInFocusedCategory;

  // Track if this is the first render of the flyout (for CSS animation)
  const [isFirstRender, setIsFirstRender] = React.useState(true);

  React.useEffect(() => {
    if (
      props.isInFocusedCategory &&
      focusAfterRender &&
      focusedThirdLevelIdx !== undefined
    ) {
      seeAllRef.current?.focus();
    }
  }, [props.isInFocusedCategory, focusAfterRender, focusedThirdLevelIdx]);

  // Mark flyout as hovered when parent nav item is focused (for lazy-rerender mode)
  React.useEffect(() => {
    if (props.isInFocusedCategory && !isFlyoutHovered(flyoutId)) {
      markFlyoutHovered(flyoutId);
    }
  }, [props.isInFocusedCategory, flyoutId, isFlyoutHovered, markFlyoutHovered]);

  // Clear first render flag after mount to allow CSS animations
  React.useEffect(() => {
    if (hasBeenHovered && isFirstRender) {
      // Use requestAnimationFrame to ensure the initial hidden state is painted before animating
      requestAnimationFrame(() => {
        setIsFirstRender(false);
      });
    }
  }, [hasBeenHovered, isFirstRender]);

  const renderableChildren = props.children.filter((subCategoryLink) =>
    hasRenderableChildren(subCategoryLink)
  );

  const displayedSubCategory = //Either the currently focused category when flyout is open, or the first one with children
    renderableChildren.find(
      (subCategoryLink: NavItem, subcategoryIndex: number) => {
        return (
          props.isInFocusedCategory &&
          subcategoryIndex === focusedSubcategoryIdx &&
          hasRenderableChildren(subCategoryLink)
        );
      }
    ) ||
    renderableChildren.find((subCategoryLink: NavItem) => {
      return hasRenderableChildren(subCategoryLink);
    });

  if (!props.children) {
    return null;
  }

  // Render full flyout content (used by FlyoutWrapper when appropriate)
  const renderFullFlyout = () => (
    <section
      className={classNames(
        'site-header-nav-flyout site-header-nav-flyout-tile-menu',
        props.className
      )}
      id={props.id}
      aria-labelledby={props.labelledBy}
      data-first-render={isFirstRender ? 'true' : undefined}
    >
      <BoundedContent
        marginTop={'7'}
        className={classNames(
          'full-width-container',
          'site-header-nav-flyout-content',
          'site-header-nav-flyout-tile-menu-content-wrapper',
          'site-header-nav-tile-menu-secondlevel-container-js'
        )}
      >
        <Box
          component="ul"
          className={classNames(
            'site-header-nav-tile-menu-menu-items',
            'site-header-nav-tile-menu-menu-items-second-level'
          )}
        >
          {renderableChildren.map(
            (subCategoryLink: NavItem, subcategoryIndex: number) => {
              return (
                <SubCategoryEntry
                  index={subcategoryIndex}
                  subCategoryLink={subCategoryLink as NavItemWithChildrenType}
                  isFocused={subCategoryLink === displayedSubCategory}
                  parentNavigationDetailPath={
                    props.parentNavigationDetailPath
                  }
                  key={subCategoryLink.id}
                />
              );
            }
          )}
        </Box>
        {displayedSubCategory && (
          <>
            <Box className="site-header-tile-menu-nav-tile-display">
              <ThirdLevelNavItemList
                subCategory={displayedSubCategory as NavItemWithChildrenType}
                categoryRenderIndex="na"
                isFocused={props.isInFocusedCategory}
                parentNavigationDetailPath={[
                  ...props.parentNavigationDetailPath,
                  ...(displayedSubCategory.analyticsId
                    ? [displayedSubCategory.analyticsId]
                    : []),
                ]}
              />
            </Box>
            {!isMxNav && (
              <SeeAllBar
                seeAllText={
                  (displayedSubCategory as NavItemWithChildrenType)
                    .seeAllCategory
                }
                seeAllUrl={displayedSubCategory.url}
                parentNavigationDetailPath={props.parentNavigationDetailPath}
                analyticsId={displayedSubCategory.analyticsId}
                level={3}
                ref={seeAllRef}
              />
            )}
          </>
        )}
      </BoundedContent>
    </section>
  );

  return (
    <FlyoutWrapper
      flyoutId={props.id || `tile-menu-flyout-${props.parentNavigationDetailPath.join('-')}`}
      children={props.children}
      renderFullFlyout={renderFullFlyout}
      ssrContainerClassName="site-header-nav-flyout"
      isInFocusedCategory={props.isInFocusedCategory}
    />
  );
};

const SubCategoryEntry = ({
  index,
  isFocused,
  subCategoryLink,
  parentNavigationDetailPath,
}: SubCategoryEntryProps) => {
  const { hoverSubNavItem, isMxNav } = useNavContext();
  const categoryListingId = React.useId();
  const categoryListingFocusedId = React.useId();
  const categoryNameId = React.useId();

  const subCategoryRef = useHoverIntent<HTMLLIElement>({
    hoverIn: () => hoverSubNavItem(index),
  });

  return (
    <li
      key={subCategoryLink.id}
      ref={subCategoryRef}
      className={classNames(
        { 'site-header-nav-flyout-focused': isFocused },
        'site-header-nav-tile-menu-second-level-element'
      )}
      data-item-title={subCategoryLink.text}
      tabIndex={0}
    >
      <button
        aria-expanded={isFocused}
        aria-controls={`${categoryListingId} ${categoryListingFocusedId}`}
        className={classNames(
          `swan-button-skin-unstyled`,
          `site-header-nav-tile-menu-category-name`,
          'site-header-nav-secondlevel-js',
          'site-header-nav-tile-menu-secondlevel-js',
          subCategoryLink.theme
        )}
        onFocus={() => {
          hoverSubNavItem(index);
        }}
      >
        <Span
          id={categoryNameId}
          className={classNames(
            'site-header-nav-tile-menu-category-heading',
            subCategoryLink.theme
          )}
          fontWeight="bold"
          fontSize="small"
        >
          {subCategoryLink.text}
          {subCategoryLink?.badge?.text && (
            <>
              <BookendsBadge {...subCategoryLink.badge} layout="visual" />
            </>
          )}
        </Span>
      </button>
      <Box
        className={classNames(
          'site-header-nav-flyout-tile-menu-subcategory',
          'site-header-nav-tile-menu-menu-items',
          'site-header-nav-tile-menu-menu-items-third-level'
        )}
      >
        <ThirdLevelNavItemList
          subCategory={subCategoryLink}
          categoryRenderIndex={index}
          parentNavigationDetailPath={[
            ...parentNavigationDetailPath,
            ...(subCategoryLink.analyticsId
              ? [subCategoryLink.analyticsId]
              : []),
          ]}
          isFocused={false}
          categoryListingId={categoryListingId}
          categoryListingFocusedId={categoryListingFocusedId}
          categoryNameId={categoryNameId}
        />
        {!isMxNav && (
          <SeeAllBar
            seeAllText={subCategoryLink.seeAllCategory}
            seeAllUrl={subCategoryLink.url}
            parentNavigationDetailPath={parentNavigationDetailPath}
            analyticsId={subCategoryLink.analyticsId}
            level={-1}
            additionalClasses={['site-header-nav-tile-menu-thirdlevel-js']}
          />
        )}
      </Box>
    </li>
  );
};

const ThirdLevelNavItemList = ({
  subCategory,
  categoryRenderIndex,
  isFocused,
  parentNavigationDetailPath,
  categoryListingId,
  categoryListingFocusedId,
  categoryNameId,
}: ThirdLevelNavItemListProps) => {
  const { focusedThirdLevelIdx, focusAfterRender } = useNavContext();
  const refs = React.useRef<React.RefObject<HTMLAnchorElement>[]>([]);

  React.useEffect(() => {
    if (isFocused && focusAfterRender && focusedThirdLevelIdx !== undefined) {
      refs.current?.[focusedThirdLevelIdx]?.current?.focus();
    }
  }, [isFocused, focusedThirdLevelIdx, focusAfterRender]);

  // Use provided IDs or fall back to subCategory.id
  const listingId = isFocused
    ? (categoryListingFocusedId || `${subCategory.id}-category-listing-focused`)
    : (categoryListingId || `${subCategory.id}-category-listing`);
  const labelId = categoryNameId || `${subCategory.id}-category-name`;

  return (
    <ul
      className={classNames('site-header-nav-tile-menu-menu-items', {
        'site-header-nav-tile-menu-menu-items-third-level-list': isFocused,
      })}
      id={listingId}
      aria-labelledby={labelId}
    >
      {subCategory.children
        ?.filter(
          (subflyoutLink: any) =>
            subflyoutLink.url && subflyoutLink.image && subflyoutLink.image.url
        )
        .map((subflyoutLink: any, itemIndex: number) => {
          return (
            itemIndex < 5 && (
              <Box
                className="site-header-nav-flyout-tile-menu-tile"
                marginLeft={'7'}
                marginTop={'7'}
                paddingBottom={'7'}
                component="li"
                key={`${subflyoutLink.id}-${categoryRenderIndex}`}
              >
                <MenuTile
                  subflyoutLink={subflyoutLink}
                  parentNavigationDetailPath={parentNavigationDetailPath}
                  isFocused={isFocused}
                  tileIndex={itemIndex}
                  addRefToList={(
                    index: number,
                    ref: React.RefObject<HTMLAnchorElement>
                  ) => {
                    refs.current[index] = ref;
                  }}
                />
              </Box>
            )
          );
        })}
    </ul>
  );
};

const MenuTile = ({
  subflyoutLink,
  isFocused,
  parentNavigationDetailPath,
  tileIndex,
  addRefToList,
}: MenuTileProps) => {
  const ref = React.useRef<HTMLAnchorElement>(null);
  addRefToList(tileIndex, ref);

  return (
    <TrackableLink
      subSection="TopNav"
      navigationDetailPath={
        subflyoutLink.analyticsId
          ? parentNavigationDetailPath.concat([subflyoutLink.analyticsId])
          : []
      }
      linkType="Visual Tile"
      ref={ref}
      href={subflyoutLink.url}
      tabIndex={0}
      dataPosition={tileIndex + 1}
      className={classNames('site-header-nav-tile-menu-thirdlevel-js', {
        'site-header-nav-thirdlevel-js': isFocused,
      })}
    >
      <StandardTile>
        {subflyoutLink?.badge?.text && (
          <StandardTileOverlay>
            <BookendsBadge {...subflyoutLink.badge} layout="visual" />
          </StandardTileOverlay>
        )}
        <StandardTileImage>
          <Image image={subflyoutLink.image} alt="" lazy />
        </StandardTileImage>
        <StandardTileContents>
          <StandardTileName>
            <Typography
              fontWeight="bold"
              className={classNames(subflyoutLink.theme)}
            >
              {subflyoutLink.text}
            </Typography>
          </StandardTileName>
          <StandardTileDescription>
            {subflyoutLink.description}
          </StandardTileDescription>
        </StandardTileContents>
      </StandardTile>
    </TrackableLink>
  );
};
